import React, { useState, useEffect } from 'react';
import { 
  FaUserAlt, 
  FaPhoneAlt, 
  FaEnvelope, 
  FaProductHunt, 
  FaBuilding, 
  FaGraduationCap, 
  FaBriefcase, 
  FaRegAddressCard, 
  FaUpload,
  FaMapMarkedAlt,
  FaVenusMars,
  FaUserTie,
  FaClock
} from 'react-icons/fa';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { API_ENDPOINTS } from '../apiConfig';

const UploadResume = () => {
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    email: '',
    salaryExpectation: '',
    education: '',
    selectedCity: '',
    selectedCenter: '',
    skill: '',
    nativeState: '',
    nativeDistrict: '',
    gender: '',
    type: '',
    experience: '',
  });

  const [resume, setResume] = useState(null);
  const [cities, setCities] = useState([]);
  const [centers, setCenters] = useState([]);
  const [resumeError, setResumeError] = useState('');

  useEffect(() => {
    axios.get(API_ENDPOINTS.GET_CITIES)
      .then(response => setCities(response.data))
      .catch(() => toast.error("Failed to load cities"));
  }, []);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    
    // Handle contact number validation
    if (id === 'contact') {
      const numbersOnly = value.replace(/\D/g, '');
      if (numbersOnly.length <= 10) {
        setFormData(prev => ({
          ...prev,
          [id]: numbersOnly
        }));
      }
      return;
    }
    
    // Handle Aadhar card validation
    if (id === 'salaryExpectation') {
      const numbersOnly = value.replace(/\D/g, '');
      if (numbersOnly.length <= 12) {
        setFormData(prev => ({
          ...prev,
          [id]: numbersOnly
        }));
      }
      return;
    }

    // Handle other fields normally
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleCityChange = async (e) => {
    const cityId = e.target.value;
    setFormData(prev => ({
      ...prev,
      selectedCity: cityId,
      selectedCenter: ''
    }));
    
    try {
      const response = await axios.get(API_ENDPOINTS.GET_CENTERS_BY_CITY(cityId));
      setCenters(response.data);
    } catch {
      toast.error("Failed to load centers");
    }
  };

  const validatePDF = (file) => {
    if (!file) return false;
    const allowedExtensions = ['pdf', 'PDF'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const allowedMimeTypes = ['application/pdf'];
    return allowedExtensions.includes(fileExtension) && allowedMimeTypes.includes(file.type);
  };

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (validatePDF(file)) {
        setResume(file);
        setResumeError('');
        toast.success('PDF file successfully selected');
      } else {
        setResume(null);
        setResumeError('Please upload only PDF files');
        toast.error('Please upload only PDF files');
        e.target.value = '';
      }
    }
  };

  const validateForm = () => {
    if (formData.contact && formData.contact.length !== 10) {
      toast.error("Contact number must be 10 digits");
      return false;
    }

    if (formData.salaryExpectation && formData.salaryExpectation.length !== 12) {
      toast.error("Aadhar card number must be 12 digits");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!validateForm()) {
      return;
    }

    if (!formData.name || !formData.contact || !formData.email || !formData.selectedCity || 
        !formData.selectedCenter || !formData.skill || !resume || !formData.gender || 
        !formData.type || !formData.education) {
      toast.error("Please fill in all mandatory fields.");
      return;
    }

    if (!resume) {
      toast.error("Please upload your resume in PDF format");
      return;
    }
  
    const formPayload = new FormData();
    
    formPayload.append('candidateName', formData.name);
    formPayload.append('phoneNumber', formData.contact);
    formPayload.append('email', formData.email);
    formPayload.append('salaryExpectation', formData.salaryExpectation)
    formPayload.append('education', formData.education);
    formPayload.append('cityId', formData.selectedCity);
    formPayload.append('centerId', formData.selectedCenter);
    formPayload.append('skillsCategory', formData.skill);
    formPayload.append('nativeState', formData.nativeState);
    formPayload.append('nativeDistrict', formData.nativeDistrict);
    formPayload.append('gender', formData.gender);
    formPayload.append('type', formData.type);
    formPayload.append('experience', formData.experience);
    formPayload.append('resume', resume);
  
    try {
      await axios.post(API_ENDPOINTS.UPLOAD_RESUME, formPayload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success("Resume uploaded successfully");
  
      setFormData({
        name: '',
        contact: '',
        email: '',
        salaryExpectation: '',
        education: '',
        selectedCity: '',
        selectedCenter: '',
        skill: '',
        nativeState: '',
        nativeDistrict: '',
        gender: '',
        type: '',
        experience: '',
      });
      setResume(null);
      setResumeError('');
      document.getElementById("resume").value = "";
    } catch (error) {
      toast.error("Failed to upload resume: " + (error.response?.data?.message || error.message));
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-md p-4 md:p-6">
        <ToastContainer />
        <h1 className="text-3xl mb-6 font-bold text-[#080366]">
            Candidates <span className="text-[#fbba00]">Registration</span>
          </h1>
        <form onSubmit={handleSubmit}>
          {/* Personal Information - Mandatory */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="mb-4 text-[#080366]">
              <label htmlFor="name" className="block font-medium mb-1">
                <FaUserAlt className="inline-block mr-2" /> Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Enter your name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </div>

            <div className="mb-4 text-[#080366]">
              <label htmlFor="contact" className="block font-medium mb-1">
                <FaPhoneAlt className="inline-block mr-2" /> Contact Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="contact"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Enter 10-digit contact number"
                value={formData.contact}
                onChange={handleInputChange}
                maxLength="10"
              />
              {formData.contact && formData.contact.length !== 10 && (
                <p className="text-red-500 text-sm mt-1">Contact number must be 10 digits</p>
              )}
            </div>

            <div className="mb-4 text-[#080366]">
              <label htmlFor="email" className="block font-medium mb-1">
                <FaEnvelope className="inline-block mr-2" /> Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </div>

            <div className="mb-4 text-[#080366]">
              <label htmlFor="gender" className="block font-medium mb-1">
                <FaVenusMars className="inline-block mr-2" /> Gender <span className="text-red-500">*</span>
              </label>
              <select
                id="gender"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={formData.gender}
                onChange={handleInputChange}
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>

            <div className="mb-4 text-[#080366]">
              <label htmlFor="education" className="block font-medium mb-1">
                <FaGraduationCap className="inline-block mr-2" />Highest Qualification<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="education"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Enter your education"
                value={formData.education}
                onChange={handleInputChange}
              />
            </div>

            <div className="mb-4 text-[#080366]">
              <label htmlFor="type" className="block font-medium mb-1">
                <FaUserTie className="inline-block mr-2" /> Type <span className="text-red-500">*</span>
              </label>
              <select
                id="type"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={formData.type}
                onChange={handleInputChange}
              >
                <option value="">Select Type</option>
                <option value="Technical">Technical</option>
                <option value="Professional">Professional</option>
              </select>
            </div>
          </div>

          {/* Skills, Experience, and Aadhar Card in One Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="mb-4 text-[#080366]">
              <label htmlFor="skill" className="block font-medium mb-1">
                <FaBriefcase className="inline-block mr-2" />Key Skills <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="skill"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Enter your skill"
                value={formData.skill}
                onChange={handleInputChange}
              />
            </div>

            <div className="mb-4 text-[#080366]">
              <label htmlFor="experience" className="block font-medium mb-1">
                <FaClock className="inline-block mr-2" /> Experience (Years)
              </label>
              <input
                type="number"
                id="experience"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Enter years of experience"
                value={formData.experience}
                onChange={handleInputChange}
                min="0"
                step="0.5"
              />
            </div>

            <div className="mb-4 text-[#080366]">
              <label htmlFor="salaryExpectation" className="block font-medium mb-1">
                <FaRegAddressCard className="inline-block mr-2" /> Aadhar Card
              </label>
              <input
                type="text"
                id="salaryExpectation"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Enter 12-digit Aadhar number"
                value={formData.salaryExpectation}
                onChange={handleInputChange}
                maxLength="12"
              />
              {formData.salaryExpectation && formData.salaryExpectation.length !== 12 && (
                <p className="text-red-500 text-sm mt-1">Aadhar number must be 12 digits</p>
              )}
            </div>
          </div>

          {/* Program and Profile Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="mb-4">
              <label htmlFor="selectedCity" className="block font-medium text-[#080366] mb-1">
                <FaProductHunt className="inline-block mr-2" /> Program <span className="text-red-500">*</span>
              </label>
              <select
                id="selectedCity"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={formData.selectedCity}
                onChange={handleCityChange}
              >
                <option value="">Select a Program</option>
                {cities.map((city) => (
                  <option key={city._id} value={city._id}>{city.name}</option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label htmlFor="selectedCenter" className="block font-medium mb-1 text-[#080366]">
                <FaBuilding className="inline-block mr-2" /> Profile <span className="text-red-500">*</span>
              </label>
              <select
                id="selectedCenter"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={formData.selectedCenter}
                onChange={handleInputChange}
              >
                <option value="">Select a Profile</option>
                {centers.map((center) => (
                  <option key={center._id} value={center._id}>{center.name}</option>
                ))}
              </select>
            </div>
          </div>
         

          {/* Native State and District - Just above Resume */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="mb-4 text-[#080366]">
              <label htmlFor="nativeState" className="block font-medium mb-1">
                <FaMapMarkedAlt className="inline-block mr-2" /> Native State
              </label>
              <input
                type="text"
                id="nativeState"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Enter your native state"
                value={formData.nativeState}
                onChange={handleInputChange}
              />
            </div>

            <div className="mb-4 text-[#080366]">
              <label htmlFor="nativeDistrict" className="block font-medium mb-1">
                <FaMapMarkedAlt className="inline-block mr-2" /> Native District
              </label>
              <input
                type="text"
                id="nativeDistrict"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Enter your native district"
                value={formData.nativeDistrict}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="mb-6 text-[#080366]">
            <label htmlFor="resume" className="block font-medium mb-1">
              <FaUpload className="inline-block mr-2" /> Resume (PDF only) <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              id="resume"
              accept=".pdf"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              onChange={handleResumeChange}
            />
            {resumeError && (
              <p className="text-red-500 text-sm mt-1">{resumeError}</p>
            )}
            <p className="text-sm text-gray-500 mt-1">
              Only PDF files are accepted. Maximum file size: 5MB
            </p>
          </div>

          <button
            type="submit"
            className="w-full md:w-auto bg-[#080366] text-white font-bold py-3 px-6 rounded hover:bg-[#fbba00] transition-colors duration-300"
          >
            Submit
          </button>
        </form>
      </div>
    </div>
  );
};

export default UploadResume;